import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ApproveDecisionRequest,
  BulkReviewDecisionRequest,
  BulkReviewResult,
  QuestionReview,
  RejectDecisionRequest,
} from '../../interfaces/question-review';
import { QuestionBankItem } from '../../interfaces/question-search';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

@Injectable({ providedIn: 'root' })
export class QuestionReviewService {
  private readonly http = inject(HttpClient);
  // `BASE_URL` contient déjà `/api` : ne jamais le rajouter ici.
  private readonly base = `${environment.BASE_URL}/question-bank`;

  /** Brouillon ou question rejetée → en attente de relecture. */
  submit(questionId: number): Observable<ApiSingleResponse<QuestionBankItem>> {
    // POST sans charge utile : l'auteur de l'action est déduit du jeton.
    return this.http.post<ApiSingleResponse<QuestionBankItem>>(
      `${this.base}/${questionId}/submit`,
      null,
    );
  }

  /** En attente de relecture → approuvée. Seule une question approuvée peut servir dans un test. */
  approve(
    questionId: number,
    comment?: string,
  ): Observable<ApiSingleResponse<QuestionBankItem>> {
    const body: ApproveDecisionRequest = { comment };
    return this.http.post<ApiSingleResponse<QuestionBankItem>>(
      `${this.base}/${questionId}/approve`,
      body,
    );
  }

  /** En attente de relecture → rejetée. Le motif est exigé par le contrat. */
  reject(
    questionId: number,
    comment: string,
  ): Observable<ApiSingleResponse<QuestionBankItem>> {
    const body: RejectDecisionRequest = { comment };
    return this.http.post<ApiSingleResponse<QuestionBankItem>>(
      `${this.base}/${questionId}/reject`,
      body,
    );
  }

  /**
   * Approuve toute une file en une seule requête.
   *
   * Une requête par question aurait déclenché l'anti-flood du back — cent
   * requêtes par minute, puis bannissement de l'IP. Le lot compte pour une.
   */
  approveBatch(
    questionIds: number[],
    comment?: string,
  ): Observable<ApiSingleResponse<BulkReviewResult>> {
    const body: BulkReviewDecisionRequest = { questionIds, comment };
    return this.http.post<ApiSingleResponse<BulkReviewResult>>(
      `${this.base}/approve-batch`,
      body,
    );
  }

  /** Rejette toute une file en une seule requête. Le motif s'applique à toutes. */
  rejectBatch(
    questionIds: number[],
    comment: string,
  ): Observable<ApiSingleResponse<BulkReviewResult>> {
    const body: BulkReviewDecisionRequest = { questionIds, comment };
    return this.http.post<ApiSingleResponse<BulkReviewResult>>(
      `${this.base}/reject-batch`,
      body,
    );
  }

  /** Historique des décisions de relecture d'une question. */
  history(
    questionId: number,
  ): Observable<ApiCollectionResponse<QuestionReview>> {
    return this.http.get<ApiCollectionResponse<QuestionReview>>(
      `${this.base}/${questionId}/reviews`,
    );
  }
}
